import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { Category } from '../router/category';
import * as fromCategoryWifiProfiles from './category-wifi-profile-configuration.reducer';
import { CategoryWifiProfileState, WifiProfileConfiguration } from './category-wifi-profile-configuration.state';

// ============================================================================
// STATE SELECTORS
// ============================================================================

export const selectCategoryWifiProfileState = createFeatureSelector<CategoryWifiProfileState>('categoryWifiProfile');

export const selectAllCategoryWifiProfiles = createSelector(
  selectCategoryWifiProfileState,
  fromCategoryWifiProfiles.selectAllCategoryWifiProfiles
);

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

// Generic selector factory functions that work with any CategoryType
// These eliminate the need to replicate selectors for every category type

export const createSelectWifiProfilesLinkedToCategory = (category: Category) => {
  return createSelector(selectAllCategoryWifiProfiles, (wifiProfiles: WifiProfileConfiguration[]) => {
    if (!category) {
      return [];
    }

    return wifiProfiles.filter(
      (wifiProfile: WifiProfileConfiguration) =>
        wifiProfile.categoryId === category.categoryId && wifiProfile.categoryType === category.categoryType
    );
  });
};

export const createSelectWifiProfilesLinkedToCategoryLoaded = (category: Category) => {
  return createSelector(
    createSelectWifiProfilesLinkedToCategory(category),
    (wifiProfiles: WifiProfileConfiguration[]) => {
      if (!category) {
        return false;
      }
      return wifiProfiles && wifiProfiles.length > 0;
    }
  );
};

export const createSelectDistinctWifiProfilesLinkedToCategory = (category: Category) => {
  return createSelector(
    createSelectWifiProfilesLinkedToCategory(category),
    (wifiProfiles: WifiProfileConfiguration[]) =>
      Array.from(new Map(wifiProfiles.map((profile) => [profile.profileId, profile])).values())
  );
};

// ============================================================================
// GENERIC SELECTORS - One-liner selectors for containers
// ============================================================================

/**
 * Generic selector for wifi profiles applied to any category type (with inheritance).
 * Automatically handles inheritance logic.
 * Deduplicates profiles by profileId (if a profile exists at multiple levels, the most specific one is kept).
 *
 * Usage in containers:
 * this.inheritedWifiProfiles$ = this.store.select(selectWifiProfilesAppliedToCategory(category));
 */
export const selectWifiProfilesAppliedToCategory = (
  category: Category
): MemoizedSelector<object, WifiProfileConfiguration[]> => {
  if (category.parent) {
    return createSelector(
      selectWifiProfilesAppliedToCategory(category.parent),
      createSelectWifiProfilesLinkedToCategory(category),
      (parent, current) => Array.from(new Map([...parent, ...current].map((i) => [i.profileId, i])).values())
    );
  } else {
    return createSelectWifiProfilesLinkedToCategory(category);
  }
};
